import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import { Button, IconButton, Loader } from '@refera/ui-web'
import * as Modal from '_components/modal/generic-modal'
import {
  AddCircle as AddCircleIcon,
  MinusCirlce as MinusCircleIcon,
  Trash as TrashIcon,
} from '@refera/ui-icons'
import Theme from '@refera/ui-core'
import { Box, Grid, Tooltip } from '@mui/material'
import {
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
} from '@material-ui/core'
import CommonSelect from '_components/common/select'
import { ControlledSwitch } from '_components/inputs'
import Autocomplete from '@material-ui/lab/Autocomplete'

import NewProviderIcon from '_assets/icons/new-provider.svg'
import Svg from '_components/svg'
import HelpInfoButton from '_/components/button/help-info-button'

import { currentServiceOrderSelector } from '_modules/service-orders/selectors'
import {
  getProviderSuggestionsListSelector,
  getProviderSuggestionsListSelectorLoading,
  providerSuggestionsSelector,
  providersResultWithoutPaginationSelector,
} from '_modules/provider/selectors'
import { getProvidersWithoutPagination } from '_modules/provider/actions'
import { REGISTER_STATUS } from '_utils/constants'
import { isAgencySaas, reorder } from '_utils/helpers'
import DragAndDrop from '_components/common/drag-and-drop'

import useStyles from './styles'
import { createTradesmanSequence } from '_modules/tradesman-sequence/actions'
import { getServiceOrder, getGenericParameters } from '_modules/service-orders/actions'
import Loading from '_/components/loading'
import { GENERIC_PARAMETERS } from '_/utils/constants/service-order'
import useIsGenericParamActive from '_/hooks/use-is-generic-param-active'

import { useDialog } from '_/hooks/use-dialog'
import { LEVEL_OPTIONS } from '_/views/provider/components/ProviderReferaDetailsModal/constants'
import { getStepStatusLog } from '_/modules/budget/actions'
import { useToast } from '_/hooks/use-toast'
import useRolePermission from '_/hooks/use-role-permission'

const CLEAR_PROVIDERS_TOOLTIP =
  'Remove os demais prestadores da lista e mantém apenas o primeiro, preservando o prestador e a rodada já selecionados.'
const SEND_REQUESTER_MESSAGE_TOOLTIP =
  'Envia uma mensagem ao solicitante avisando que um novo prestador entrará em contato. ' +
  'Use quando existir "Visita agendada" sem "Orçamento recebido" do prestador.'

const EMPTY_ITEM = { name: null, value: null }
const ROUNDS_OPTIONS = [
  { label: '0', value: 0 },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
]

const INITIAL_STATE = {
  tradesman: [EMPTY_ITEM, EMPTY_ITEM, EMPTY_ITEM, EMPTY_ITEM],
  sendRequesterMessage: false,
}

const DUPLICATE_PROVIDER_MESSAGE = 'Este prestador já está na lista.'

const getProviderTradesmanId = provider => {
  if (!provider) return null
  const id = provider.tradesmanId ?? provider.id
  return id != null ? Number(id) : null
}

const getDuplicateProviderIndices = tradesman => {
  const indices = new Set()
  const seen = new Map()

  tradesman?.forEach((field, index) => {
    const providerId = getProviderTradesmanId(field?.value)
    if (providerId == null) return

    if (seen.has(providerId)) {
      indices.add(seen.get(providerId))
      indices.add(index)
    } else {
      seen.set(providerId, index)
    }
  })

  return indices
}

const AddProviderQueueModal = ({ handleClose }) => {
  const dispatch = useDispatch()

  const serviceOrder = useSelector(currentServiceOrderSelector)
  const providersList = useSelector(providersResultWithoutPaginationSelector)
  const providerSuggestionsList = useSelector(getProviderSuggestionsListSelector)
  const isCategoriesOptionsLoading = useSelector(getProviderSuggestionsListSelectorLoading)

  const { showToast } = useToast()
  const { isAdmin } = useRolePermission()
  const [isLoading, setIsLoading] = useState(false)
  const [rounds, setRounds] = useState([EMPTY_ITEM])
  const suggestedProviders = useSelector(providerSuggestionsSelector)
  const distributionActive = useIsGenericParamActive({
    name: GENERIC_PARAMETERS.DISTRIBUTION,
  })
  const [selectFilter, setSelectFilter] = useState('full')
  const { showDialog, closeDialog } = useDialog()

  const isSaas = isAgencySaas(serviceOrder?.agency)

  const providerSuggestionsListOptions = useMemo(() => {
    if (selectFilter === 'full') {
      const defaultSuggestionsRounds = providerSuggestionsList?.map((item, index) => {
        return { name: `rounds.${index}`, value: isSaas ? 1 : item.round }
      })
      setRounds(defaultSuggestionsRounds)

      return {
        tradesman: providerSuggestionsList?.map((item, index) => {
          return { name: `tradesman.${index}`, value: item }
        }),
        rounds: defaultSuggestionsRounds,
      }
    }

    if (selectFilter === 'category') {
      return {
        tradesman: providersList
          .filter(provider => provider?.mainServices.some(item => item.toJS().parent === null))
          .map((item, index) => ({ name: `tradesman.${index}`, value: item })),
      }
    }

    if (selectFilter === 'subcategory') {
      return {
        tradesman: providersList
          .filter(provider => provider?.mainServices.some(item => item.toJS().parent !== null))
          .map((item, index) => ({ name: `tradesman.${index}`, value: item })),
      }
    }

    return { tradesman: {} }
  }, [providerSuggestionsList, selectFilter])

  const { control, getValues, watch, handleSubmit, reset, ...methods } = useForm({
    defaultValues:
      providerSuggestionsListOptions?.tradesman?.length > 0
        ? providerSuggestionsListOptions
        : INITIAL_STATE,
  })

  const {
    fields: tradesmanFields,
    append: addTradesman,
    remove: removeTradesman,
  } = useFieldArray({
    control,
    name: 'tradesman',
  })

  const poolAvailable = useMemo(() => {
    return !!serviceOrder?.runPool
  }, [serviceOrder])

  const tradesmanValues = watch('tradesman')

  const duplicateProviderIndices = useMemo(
    () => getDuplicateProviderIndices(tradesmanValues),
    [tradesmanValues]
  )

  const hasDuplicateProviders = duplicateProviderIndices.size > 0

  const canOfferRequesterMessage = useMemo(() => {
    if (isSaas) return false

    return Boolean(
      serviceOrder?.get?.('canOfferRequesterMessageOnProviderAssignment') ??
        serviceOrder?.canOfferRequesterMessageOnProviderAssignment
    )
  }, [serviceOrder, isSaas])

  const styles = useStyles({ poolAvailable, multipleTradesman: tradesmanFields.length > 1 })

  const getDefaultRoundForIndex = useCallback(
    index => {
      if (!poolAvailable) return null
      if (isSaas) return 1
      return index < 5 ? 1 : 2
    },
    [isSaas, poolAvailable]
  )

  const addRounds = () => {
    setRounds(prev => {
      const nextIndex = prev.length
      return [...prev, { name: `rounds.${nextIndex}`, value: getDefaultRoundForIndex(nextIndex) }]
    })
  }

  const removeRounds = index => {
    const newRounds = rounds.filter((_, i) => i !== index)
    setRounds(newRounds)
  }

  const handleAppendItem = useCallback(() => {
    addTradesman(EMPTY_ITEM)
    addRounds()
  }, [])

  const handleRemoveItem = useCallback(
    event => {
      const { index } = event.currentTarget.dataset
      removeTradesman(index)
      removeRounds(index)
    },
    [removeTradesman, removeRounds]
  )

  const handleClearProviders = useCallback(() => {
    for (let i = tradesmanFields.length - 1; i >= 1; i -= 1) {
      removeTradesman(i)
    }
    setRounds(prev => (prev.length > 0 ? [prev[0]] : [EMPTY_ITEM]))
  }, [tradesmanFields.length, removeTradesman])

  const canRemoveItem = useMemo(() => tradesmanFields.length > 1, [tradesmanFields])

  const providerOptions = useMemo(() => {
    if (selectFilter === 'full') {
      return providersList?.map(item => ({
        value: item,
        name: item.name,
        label: item.name,
      }))
    }

    if (selectFilter === 'category') {
      return providersList
        .filter(provider => provider?.mainServices.some(item => item.toJS().parent === null))
        .map(item => ({
          value: item,
          name: item.name,
          label: item.name,
        }))
    }

    if (selectFilter === 'subcategory') {
      return providersList
        .filter(provider => provider?.mainServices.some(item => item.toJS().parent !== null))
        .map(item => ({
          value: item,
          name: item.name,
          label: item.name,
        }))
    }

    return []
  }, [providersList, selectFilter])

  const getOptionLabel = useCallback(
    option => {
      return (
        providerOptions?.find(opt => opt?.value?.tradesmanId === option?.value?.tradesmanId)
          ?.label || ''
      )
    },
    [providerOptions, selectFilter]
  )

  const getOptionSelected = useCallback(
    (option, currentValue) => {
      return Number(option?.value?.tradesmanId) === Number(currentValue?.value?.tradesmanId)
    },
    [selectFilter]
  )

  const onInputChange = useCallback(
    (inputLabel, name, onChange, index) => {
      const isValidOption = providerOptions.find(option => option.name === inputLabel) || null

      if (isValidOption) {
        const suggestedProvider = providerSuggestionsListOptions?.tradesman?.find(
          item => item.name === name
        )?.value
        const roundName = rounds?.[index]?.name
        const roundNameIndex = roundName?.split('.')?.[1]
        const changedRoundIndex = roundNameIndex !== String(index)

        if (suggestedProvider && Object.hasOwn(suggestedProvider, 'round')) {
          if (changedRoundIndex) {
            setRounds(prevState => {
              const newRounds = [...prevState]
              newRounds[index] = { ...prevState[index], name: `rounds.${index}` }
              return newRounds
            })
          }
        }

        onChange({ name, value: isValidOption.value })
      }
      if (inputLabel === '') {
        setRounds(prevState => {
          const newRounds = [...prevState]
          newRounds[index] = { name: `rounds.${index}`, value: '' }
          return newRounds
        })
        onChange({ name: null, value: EMPTY_ITEM })
      }
    },
    [providerOptions, providerSuggestionsListOptions, rounds]
  )

  const handleRoundsChange = (roundItem, index) => {
    const { target } = roundItem
    setRounds(prevState => {
      const newRounds = [...prevState]
      newRounds[index] = { name: `rounds.${index}`, value: target.value }
      return newRounds
    })
  }

  const renderSelect = useCallback(
    (item, index) => (
      <>
        <Grid item className={styles.fullWidth} display="flex" gap="1rem" alignItems="flex-start">
          <div style={{ flex: 1 }}>
            <Typography className={styles.label}>Selecione o prestador</Typography>
            <div
              className={
                duplicateProviderIndices.has(index)
                  ? styles.providerSelectWrapperWithError
                  : styles.providerSelectWrapper
              }
            >
              <Controller
                control={control}
                name={`tradesman.${index}`}
                render={({ onChange, name, ...fieldProps }) => {
                  return (
                    <Autocomplete
                      {...fieldProps}
                      id={name}
                      options={providerOptions}
                      onChange={(event, newValue) => {
                        if (!newValue?.value) {
                          return
                        }

                        const provider = newValue.value

                        if (poolAvailable) {
                          const roundValue =
                            provider.round != null ? provider.round : getDefaultRoundForIndex(index)

                          setRounds(prev => {
                            const newRounds = [...prev]
                            newRounds[index] = { name: `rounds.${index}`, value: roundValue }
                            return newRounds
                          })
                        }

                        onChange({ name, value: provider })
                      }}
                      onInputChange={(event, newInputValue) => {
                        return onInputChange(newInputValue, name, onChange, index)
                      }}
                      multiple={false}
                      getOptionLabel={getOptionLabel}
                      getOptionSelected={getOptionSelected}
                      disableClearable
                      renderInput={params => (
                        <TextField {...params} error={duplicateProviderIndices.has(index)} />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option?.value?.tradesmanId === value?.value?.tradesmanId
                      }
                      renderOption={props => {
                        const { level, servicesCount, warrantyPercentage, budgetConversion } =
                          props.value?.score || {}

                        const levelLabel = level
                          ? LEVEL_OPTIONS.find(itemLevel => itemLevel.value === level)
                          : null

                        if (!level || !levelLabel) {
                          return (
                            <Tooltip
                              placement="left"
                              arrow
                              classes={{ popper: styles.tooltip }}
                              title={
                                <Box className={[styles.tooltipBox, styles.center]}>
                                  <Svg className={styles.svgIcon} icon={NewProviderIcon} />
                                  <Typography variant="body2">Prestador novo</Typography>
                                </Box>
                              }
                            >
                              <li {...props} className={styles.li}>
                                {props.label}
                              </li>
                            </Tooltip>
                          )
                        }

                        return (
                          <Tooltip
                            placement="left"
                            arrow
                            classes={{ popper: styles.tooltip }}
                            title={
                              <Box className={styles.tooltipBox}>
                                <Typography variant="body2">
                                  <strong>Classificação:</strong> {levelLabel.label}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Qtd. de serviços na Refera:</strong> {servicesCount}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>% de garantias:</strong> {warrantyPercentage}%
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Conv. de orçamentos:</strong> {budgetConversion}%
                                </Typography>
                              </Box>
                            }
                          >
                            <li {...props} className={styles.li}>
                              {props.label}
                            </li>
                          </Tooltip>
                        )
                      }}
                    />
                  )
                }}
              />
              {duplicateProviderIndices.has(index) && (
                <FormHelperText error className={styles.duplicateProviderError}>
                  {DUPLICATE_PROVIDER_MESSAGE}
                </FormHelperText>
              )}
            </div>
          </div>
          {poolAvailable && (
            <CommonSelect
              name="rounds"
              label="Rodada"
              defaultValue={rounds[index]?.value || 0}
              value={rounds[index]?.value || 0}
              onChange={roundItem => handleRoundsChange(roundItem, index)}
              options={ROUNDS_OPTIONS}
              labelClassName={styles.customSelectLabel}
              selectStyles={styles.customSelect}
            />
          )}
        </Grid>
        {canRemoveItem && (
          <Grid item classes={{ item: styles.deleteButtonContainer }}>
            <IconButton
              variant="ghost"
              onClick={handleRemoveItem}
              data-index={index}
              className={styles.deleteButton}
            >
              <MinusCircleIcon color={Theme.Colors.Red.Base} />
            </IconButton>
          </Grid>
        )}
      </>
    ),
    [
      canRemoveItem,
      control,
      handleRemoveItem,
      providerOptions,
      getOptionLabel,
      getOptionSelected,
      providerSuggestionsListOptions,
      poolAvailable,
      rounds,
      getDefaultRoundForIndex,
      duplicateProviderIndices,
    ]
  )

  const handleSelect = useCallback(
    value => {
      setSelectFilter(value)
      updateList()
    },
    [selectFilter]
  )

  const updateList = useCallback(
    (startIndex, endIndex) => {
      if (startIndex === endIndex) return

      const { tradesman: tradesmanValue, runPool, sendRequesterMessage } = getValues()
      const reorderedTradesman = reorder(tradesmanValue, startIndex, endIndex)
      const reorderedRounds = reorder(rounds, startIndex, endIndex)

      reset(
        { tradesman: reorderedTradesman, runPool, sendRequesterMessage },
        {
          keepErrors: true,
          keepDirty: true,
          keepTouched: true,
          keepSubmitCount: true,
        }
      )
      setRounds(reorderedRounds)
    },
    [getValues, reset, rounds]
  )

  const convertToSlug = useCallback(
    text =>
      text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replaceAll(' ', '_'),
    []
  )

  const getTradesmanIncludesCurrentAgencyInBlockedAgencies = tradesmanIdsList => {
    const currentAgencyId = serviceOrder?.agency?.get('id')

    for (const tradesmanId of tradesmanIdsList) {
      const tradesman = providersList.find(provider => provider?.id === tradesmanId)

      if (tradesman?.mainBlockedAgencies?.includes(currentAgencyId)) {
        return tradesman.name
      }
    }

    return undefined
  }

  const handleSubmitFunction = useCallback(
    async data => {
      setIsLoading(true)
      let shouldCloseModal = false
      const refreshAfterSubmit = () => {
        Promise.resolve()
          .then(() =>
            Promise.allSettled([
              dispatch(getServiceOrder(serviceOrder.id)),
              dispatch(getStepStatusLog(serviceOrder.id)),
            ])
          )
          .then(results => {
            const rejectedResult = results.find(result => result.status === 'rejected')
            if (rejectedResult) {
              console.error('[AddProviderQueueModal][refreshAfterSubmit]: ', rejectedResult.reason)
            }
          })
          .catch(refreshError => {
            console.error('[AddProviderQueueModal][refreshAfterSubmit]: ', refreshError)
          })
      }

      try {
        const candidates = data.tradesman.filter(field => field.name).map(field => field.value.id)

        const candidateThatIncludesCurrentAgencyAsBlocked =
          getTradesmanIncludesCurrentAgencyInBlockedAgencies(candidates)

        if (candidateThatIncludesCurrentAgencyAsBlocked) {
          showToast({
            type: 'error',
            message: `O prestador ${candidateThatIncludesCurrentAgencyAsBlocked} não pode atender a intermediária do chamado, conforme configurado em seu cadastro. Para prosseguir, selecione outro prestador.`,
          })
          return
        }

        const roundsValues = rounds?.map(round => round?.value).filter(round => round !== '')

        await dispatch(
          createTradesmanSequence(serviceOrder.id, {
            isActive: true,
            serviceOrder: serviceOrder.id,
            candidates,
            suggestedProviders,
            runPool: data.runPool,
            rounds: roundsValues,
            ...(canOfferRequesterMessage && data.sendRequesterMessage
              ? { sendRequesterMessage: true }
              : {}),
          })
        )

        shouldCloseModal = true
      } catch (error) {
        showToast({
          type: 'error',
          message: error?.[0] ?? error?.error_message ?? null,
        })
      } finally {
        setIsLoading(false)
      }

      if (shouldCloseModal) {
        refreshAfterSubmit()
        handleClose({ reason: 'success' })
      }
    },
    [serviceOrder.id, handleClose, suggestedProviders, rounds, canOfferRequesterMessage]
  )

  const handleSave = useCallback(() => {
    if (serviceOrder?.isVipClient) {
      showDialog({
        type: 'info',
        subject: 'Você tem certeza que deseja fazer o pool para o cliente VIP?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          handleSubmit(handleSubmitFunction)()
          closeDialog()
        },
        onCancel: () => {
          closeDialog()
        },
      })
      return
    }

    handleSubmit(handleSubmitFunction)()
  }, [serviceOrder?.isVipClient, rounds])

  const disableSaveButton = useMemo(
    () =>
      isLoading ||
      tradesmanValues?.every(field => !field?.name) ||
      !Object.keys(getValues()).length ||
      hasDuplicateProviders,
    [isLoading, tradesmanValues, hasDuplicateProviders]
  )

  const defaultFilters = useMemo(() => {
    return serviceOrder
      ? {
          ...(serviceOrder.getIn(['property', 'city']) && {
            cities: convertToSlug(serviceOrder.getIn(['property', 'city'])),
          }),
          ...(serviceOrder.category?.first?.()?.get('id') && {
            mainServices: serviceOrder.category?.first?.()?.get('id'),
          }),
          registrationStatus: REGISTER_STATUS.COMPLETE,
          serviceOrder: serviceOrder.id,
          referaSuggestion: false,
        }
      : {}
  }, [serviceOrder])

  const fetchProviders = useCallback(() => {
    if (!distributionActive) {
      dispatch(
        getGenericParameters({
          name: GENERIC_PARAMETERS.DISTRIBUTION,
        })
      ).then(data => {
        if (!data?.[0]?.active) {
          dispatch(getProvidersWithoutPagination(defaultFilters))
        }
      })
    }
  }, [dispatch])

  useEffect(() => {
    !distributionActive && fetchProviders()
  }, [fetchProviders, dispatch])

  const listProps = useMemo(
    () => ({
      item: true,
      xs: 12,
      classes: { item: styles.fieldList },
      style: { gap: '2.4rem' },
    }),
    [styles.fieldList]
  )

  const itemProps = useMemo(() => ({
    item: true,
    xs: 12,
    columnSpacing: '24',
    wrap: 'nowrap',
  }))

  const customOnChange = useCallback(
    (event, callback) => {
      setIsLoading(true)

      dispatch(
        getProvidersWithoutPagination({
          ...defaultFilters,
          referaSuggestion: event.target.checked,
        })
      )
        .then(() => {
          setIsLoading(false)
          callback()
        })
        .catch(() => {
          setIsLoading(false)
          callback()
        })
    },
    [dispatch, defaultFilters]
  )

  if (isCategoriesOptionsLoading) {
    return <Loading />
  }

  return (
    <>
      <form id="add-providers-queue">
        <Modal.Root open onClose={handleClose} disableEnforceFocus>
          <Modal.TitleModal>
            <Typography className={styles.title}>Selecionar prestador</Typography>
          </Modal.TitleModal>
          <Modal.Content className={styles.modalContent}>
            <Grid item xs={12}>
              {!serviceOrder?.isReferaService &&
                serviceOrder?.agency?.get('contractType') === 'SAAS' && (
                  <ControlledSwitch
                    name="referaSuggestions"
                    label="Mostrar sugestões da Refera"
                    control={control}
                    className={styles.showReferaSuggestions}
                    customOnChange={customOnChange}
                    defaultValue={false}
                  />
                )}
              <Box className={styles.descriptionRow}>
                <Typography>
                  Selecione os prestadores que vão participar da sequência de aceite desse chamado,
                  por ordem de prioridade.{' '}
                  {/* <Link href="/#" title="Sobre sequenciamento de prestador" underline="always">
                    Clique aqui para saber mais
                  </Link> */}
                </Typography>
                {isAdmin && (
                  <Tooltip
                    title={CLEAR_PROVIDERS_TOOLTIP}
                    placement="bottom"
                    enterDelay={300}
                    leaveDelay={0}
                    disableInteractive
                  >
                    <span className={styles.clearProvidersTooltipWrap}>
                      <Button
                        variant="ghost"
                        startIcon={<TrashIcon color={Theme.Colors.Red.Base} />}
                        onClick={handleClearProviders}
                        disabled={tradesmanFields.length <= 1}
                        className={styles.clearProvidersButton}
                      >
                        Limpar
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} display="flex">
              <FormControl className={styles.selectWrapper}>
                <Typography id="providerLabel" className={styles.selectLabel}>
                  Filtro
                </Typography>
                <Select
                  id="providerFilter"
                  labelId="providerLabel"
                  className={styles.select}
                  value={selectFilter}
                  defaultValue="full"
                  onChange={e => handleSelect(e.target.value)}
                >
                  <MenuItem key="1" value="full">
                    Completo
                  </MenuItem>
                  <MenuItem key="2" value="subcategory">
                    Subcategorias
                  </MenuItem>
                  <MenuItem key="3" value="category">
                    Só categorias
                  </MenuItem>
                </Select>
              </FormControl>

              {poolAvailable && (
                <ControlledSwitch
                  name="runPool"
                  label="Executar pool"
                  control={control}
                  className={styles.runPool}
                  defaultValue
                />
              )}
            </Grid>

            <FormProvider {...{ control, getValues, watch, handleSubmit, reset, ...methods }}>
              <DragAndDrop
                id="add-provider-queue-modal"
                list={tradesmanFields}
                renderItem={tradesmanFields?.length > 0 ? renderSelect : null}
                updateList={updateList}
                listProps={listProps}
                itemProps={itemProps}
              />
            </FormProvider>

            <Grid item xs={12} className={styles.addProviderContainer}>
              <Button
                variant="ghost"
                startIcon={<AddCircleIcon color={Theme.Colors.Primary.Base} />}
                onClick={handleAppendItem}
              >
                Adicionar outro prestador
              </Button>
            </Grid>
          </Modal.Content>
          <Modal.Actions className={styles.actions}>
            <div className={styles.requesterMessageAction}>
              {canOfferRequesterMessage && (
                <>
                  <div className={styles.sendRequesterMessageHeader}>
                    <Typography className={styles.sendRequesterMessageLabel}>
                      Enviar mensagem ao solicitante
                    </Typography>
                    <HelpInfoButton title={SEND_REQUESTER_MESSAGE_TOOLTIP} placement="bottom" />
                  </div>
                  <ControlledSwitch
                    name="sendRequesterMessage"
                    label=""
                    control={control}
                    className={styles.sendRequesterMessage}
                    defaultValue={false}
                  />
                </>
              )}
            </div>
            <div className={styles.actionButtons}>
              <Modal.ButtonRed onClick={handleClose}>Cancelar</Modal.ButtonRed>
              <Modal.ButtonFullBlue
                form="add-providers-queue"
                onClick={handleSave}
                disabled={disableSaveButton}
              >
                Salvar
              </Modal.ButtonFullBlue>
            </div>
          </Modal.Actions>
        </Modal.Root>
      </form>
      <Loader hasBackdrop open={isLoading} label="Aguarde..." />
    </>
  )
}

AddProviderQueueModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  // isOpen: PropTypes.bool.isRequired,
}

export default React.memo(AddProviderQueueModal)
